import React, { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { RefreshCw, Download, Plus, Minus, Info } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const reportOptions = [
  { value: '', label: 'Choose a report...' },
  { value: 'project-source-summary', label: 'Project by Source - Summary' },
  { value: 'project-source-detail', label: 'Project by Source - Detail' },
  { value: 'reconstruction-note-tracking', label: 'Reconstruction Project Note Tracking' },
  { value: 'mitigation-note-tracking', label: 'Mitigation Project Note Tracking' },
];

const daysOptions = [
  { value: 1, label: 'Past 1+ day' },
  { value: 5, label: 'Past 5+ days' },
  { value: 7, label: 'Past 7+ days' },
  { value: 14, label: 'Past 14+ days' },
  { value: 30, label: 'Past 30+ days' },
  { value: 60, label: 'Past 60+ days' },
  { value: 90, label: 'Past 90+ days' },
  { value: 180, label: 'Past 180+ days' },
  { value: 365, label: 'Past 365+ days' },
];

/* Reusable searchable dropdown */
const SearchableDropdown = ({ options, value, onChange, placeholder, testIdPrefix, filterKey = 'label' }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setFilter(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => o[filterKey].toLowerCase().includes(filter.toLowerCase()));
  const selectedLabel = options.find(o => o.value === value)?.[filterKey] || placeholder;

  return (
    <div className="relative" ref={ref} data-testid={`${testIdPrefix}-wrapper`}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full border border-gray-300 rounded px-3 py-2.5 text-left text-sm bg-white flex items-center justify-between focus:outline-none focus:border-[#6BADE0] focus:ring-1 focus:ring-[#6BADE0]"
        data-testid={`${testIdPrefix}-trigger`}>
        <span className={value !== null && value !== undefined ? 'text-gray-800' : 'text-gray-400'}>{selectedLabel}</span>
        <span className="text-gray-400 text-xs">&#9660;</span>
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#6BADE0] rounded shadow-lg" data-testid={`${testIdPrefix}-panel`}>
          <div className="p-2 border-b border-gray-200">
            <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#6BADE0]" autoFocus data-testid={`${testIdPrefix}-filter`} />
          </div>
          <ul className="max-h-48 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
            {filtered.map(o => (
              <li key={o.value} onClick={() => { onChange(o.value); setOpen(false); setFilter(''); }}
                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${value === o.value ? 'bg-[#5B9BD5] text-white' : 'text-gray-700 hover:bg-[#5B9BD5] hover:text-white'}`}
                data-testid={`${testIdPrefix}-opt-${o.value}`}>
                {o[filterKey]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* Multi-select searchable dropdown for project types */
const MultiSelectDropdown = ({ options, selected, onChange, placeholder, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setFilter(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => o.name.toLowerCase().includes(filter.toLowerCase()));
  const displayText = selected.length > 0 ? selected.join(', ') : placeholder;

  const toggleItem = (name) => {
    if (selected.includes(name)) {
      onChange(selected.filter(s => s !== name));
    } else {
      onChange([...selected, name]);
    }
  };

  return (
    <div className="relative" ref={ref} data-testid={`${testIdPrefix}-wrapper`}>
      <div onClick={() => setOpen(!open)}
        className="w-full border border-gray-300 rounded px-3 py-2.5 text-left text-sm bg-white cursor-pointer min-h-[40px] flex items-center"
        data-testid={`${testIdPrefix}-trigger`}>
        <span className={selected.length > 0 ? 'text-gray-800 truncate' : 'text-gray-400'}>{displayText}</span>
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#6BADE0] rounded shadow-lg" data-testid={`${testIdPrefix}-panel`}>
          <div className="p-2 border-b border-gray-200">
            <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search..." className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#6BADE0]" autoFocus data-testid={`${testIdPrefix}-filter`} />
          </div>
          <ul className="max-h-48 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
            {filtered.map(o => (
              <li key={o.name} onClick={() => toggleItem(o.name)}
                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${selected.includes(o.name) ? 'bg-[#5B9BD5] text-white' : 'text-gray-700 hover:bg-[#5B9BD5] hover:text-white'}`}
                data-testid={`${testIdPrefix}-opt-${o.name}`}>
                {o.name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const dropdownRef = useRef(null);

  // Summary report state
  const [summaryData, setSummaryData] = useState([]);
  const [summaryTotal, setSummaryTotal] = useState(0);

  // Detail report state
  const [detailSources, setDetailSources] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [expandedSources, setExpandedSources] = useState({});

  // Note tracking state
  const [projectTypes, setProjectTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedDays, setSelectedDays] = useState(5);
  const [noteData, setNoteData] = useState(null); // null = not loaded, [] = empty
  const [noteLoaded, setNoteLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('2025-01-01');
  const [dateTo, setDateTo] = useState('2026-03-31');

  // Fetch project types on mount
  useEffect(() => {
    axios.get(`${API_URL}/project-types`).then(res => {
      setProjectTypes(res.data.project_types || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setFilterText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = reportOptions.filter((opt) =>
    opt.label.toLowerCase().includes(filterText.toLowerCase())
  );

  const selectedLabel = reportOptions.find((o) => o.value === selectedReport)?.label || 'Choose a report...';

  const handleSelect = (value) => {
    setSelectedReport(value);
    setIsOpen(false);
    setFilterText('');
    // Clear all report state when switching
    setSummaryData([]);
    setSummaryTotal(0);
    setDetailSources([]);
    setDetailTotal(0);
    setExpandedSources({});
    setSelectedTypes([]);
    setSelectedDays(5);
    setNoteData(null);
    setNoteLoaded(false);
    if (value === 'project-source-summary' || value === 'project-source-detail') {
      loadReport(value);
    }
  };

  const loadReport = useCallback(async (reportType) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      if (reportType === 'project-source-summary') {
        const response = await axios.get(`${API_URL}/reports/project-source-summary?${params.toString()}`);
        setSummaryData(response.data.data || []);
        setSummaryTotal(response.data.total || 0);
      } else if (reportType === 'project-source-detail') {
        const response = await axios.get(`${API_URL}/reports/project-source-detail?${params.toString()}`);
        setDetailSources(response.data.sources || []);
        setDetailTotal(response.data.total_entries || 0);
        setExpandedSources({});
      }
    } catch (err) {
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  const loadNoteTracking = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedTypes.length > 0) params.append('types', selectedTypes.join(','));
      params.append('days', selectedDays);
      params.append('report_type', selectedReport === 'reconstruction-note-tracking' ? 'reconstruction' : 'mitigation');
      const response = await axios.get(`${API_URL}/reports/note-tracking?${params.toString()}`);
      setNoteData(response.data.data || []);
      setNoteLoaded(true);
    } catch (err) {
      console.error('Error loading note tracking:', err);
      setNoteData([]);
      setNoteLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [selectedTypes, selectedDays, selectedReport]);

  const handleLoad = () => {
    if (selectedReport === 'reconstruction-note-tracking' || selectedReport === 'mitigation-note-tracking') {
      loadNoteTracking();
    } else {
      loadReport(selectedReport);
    }
  };

  const triggerDownload = (wb, filename) => {
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadSummary = () => {
    const wb = XLSX.utils.book_new();
    const rows = [
      ['PROJECTS BY SOURCE - SUMMARY'],
      [`Report from ${dateFrom} 00:00:00 to ${dateTo} 23:59:59`],
      [],
      ['Source', 'Projects'],
      ...summaryData.map(row => [row.source, row.projects]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 40 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    triggerDownload(wb, 'MyReportExcel.xlsx');
  };

  const handleDownloadDetail = () => {
    const wb = XLSX.utils.book_new();
    const rows = [
      ['PROJECTS BY SOURCE - DETAIL'],
      [`Report from ${dateFrom} 00:00:00 to ${dateTo} 23:59:59`],
      [],
      ['Project Number', 'Created By', 'Created On'],
    ];
    detailSources.forEach(src => {
      rows.push([`${src.source.toUpperCase()} - ${src.count} project(s)`]);
      src.projects.forEach(p => {
        rows.push([p.project_number, p.created_by, p.created_on]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 40 }, { wch: 25 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Detail');
    triggerDownload(wb, 'MyReportExcel.xlsx');
  };

  const handleDownloadNoteTracking = async () => {
    let data = noteData;
    if (!data) {
      // Auto-load data first
      try {
        const params = new URLSearchParams();
        if (selectedTypes.length > 0) params.append('types', selectedTypes.join(','));
        params.append('days', selectedDays);
        params.append('report_type', selectedReport === 'reconstruction-note-tracking' ? 'reconstruction' : 'mitigation');
        const response = await axios.get(`${API_URL}/reports/note-tracking?${params.toString()}`);
        data = response.data.data || [];
        setNoteData(data);
        setNoteLoaded(true);
      } catch (err) {
        console.error('Error loading data for download:', err);
        return;
      }
    }
    const title = selectedReport === 'reconstruction-note-tracking' ? 'RECONSTRUCTION PROJECT NOTE TRACKING' : 'MITIGATION PROJECT NOTE TRACKING';
    const wb = XLSX.utils.book_new();
    const rows = [
      [title],
      [`Past ${selectedDays}+ days | Types: ${selectedTypes.length > 0 ? selectedTypes.join(', ') : 'All'}`],
      [],
      ['Project ID', 'Customer', 'Job Type', 'Job Location', 'Last Note/Action', 'Notes'],
    ];
    data.forEach(p => {
      rows.push([p.project_id, p.customer, p.job_type, `${p.job_location} ${p.address}`, p.last_note_action, p.notes]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 18 }, { wch: 25 }, { wch: 15 }, { wch: 30 }, { wch: 22 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Note Tracking');
    triggerDownload(wb, 'MyReportExcel.xlsx');
  };

  const toggleSource = (sourceIdx) => {
    setExpandedSources(prev => ({ ...prev, [sourceIdx]: !prev[sourceIdx] }));
  };

  const getPageTitle = () => {
    if (selectedReport === 'project-source-summary') return 'Projects By Source - Summary';
    if (selectedReport === 'project-source-detail') return 'Projects By Source - Detail';
    if (selectedReport === 'reconstruction-note-tracking') return 'Reconstruction Project Note Tracking';
    if (selectedReport === 'mitigation-note-tracking') return 'Mitigation Project Note Tracking';
    return 'All Reports';
  };

  const isNoteTracking = selectedReport === 'reconstruction-note-tracking' || selectedReport === 'mitigation-note-tracking';
  const noteTrackingTitle = selectedReport === 'reconstruction-note-tracking' ? 'Reconstruction Project Note Tracking' : 'Mitigation Project Note Tracking';

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-base lg:text-lg text-[#5B8DB8] font-light" data-testid="reports-subtitle">Reports</h2>
          <h1 className="text-2xl lg:text-3xl text-[#5B8DB8] font-light italic" data-testid="reports-title">
            {getPageTitle()}
          </h1>
        </div>

        {/* Report Selector Card */}
        <div className="bg-white rounded shadow p-8 lg:p-12 overflow-visible" data-testid="reports-card">
          <div className="max-w-2xl mx-auto">
            <label className="block text-sm font-semibold text-gray-800 mb-2" data-testid="select-label">
              Select a Report:
            </label>
            <div className="relative" ref={dropdownRef} data-testid="report-dropdown">
              <button type="button" onClick={() => setIsOpen(!isOpen)}
                className="w-full border border-gray-300 rounded px-4 py-3 text-left text-sm bg-white flex items-center justify-between focus:outline-none focus:border-[#6BADE0] focus:ring-1 focus:ring-[#6BADE0]"
                data-testid="report-select-trigger">
                <span className={selectedReport ? 'text-gray-800' : 'text-gray-400'}>{selectedLabel}</span>
                <span className="text-gray-400 text-xs">&#9660;</span>
              </button>
              {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-[#6BADE0] rounded shadow-lg" data-testid="report-dropdown-panel">
                  <div className="p-2 border-b border-gray-200">
                    <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#6BADE0]" autoFocus data-testid="report-filter-input" />
                  </div>
                  <ul className="max-h-64 overflow-y-auto" data-testid="report-options-list">
                    {filteredOptions.map((option) => (
                      <li key={option.value} onClick={() => handleSelect(option.value)}
                        className={`px-4 py-3 text-sm cursor-pointer transition-colors ${selectedReport === option.value ? 'bg-[#5B9BD5] text-white' : 'text-gray-700 hover:bg-[#5B9BD5] hover:text-white'}`}
                        data-testid={`report-option-${option.value || 'default'}`}>
                        {option.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Report */}
        {selectedReport === 'project-source-summary' && (
          <div className="bg-white rounded shadow p-8 lg:p-12 mt-4" data-testid="project-source-summary-view">
            <div className="max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-1" data-testid="report-section-title">Projects by Source - Summary</h3>
              <p className="text-sm text-gray-600 mb-4" data-testid="report-section-desc">Select the start and end dates for the range you want to analyze.</p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-2 bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm">
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-transparent border-none outline-none text-sm text-gray-700" data-testid="summary-date-from" />
                  <span className="text-gray-400">-</span>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-transparent border-none outline-none text-sm text-gray-700" data-testid="summary-date-to" />
                </div>
                <button onClick={handleLoad} disabled={loading} className="flex items-center gap-2 bg-[#28a745] hover:bg-[#218838] text-white text-sm font-medium px-4 py-2 rounded transition-colors" data-testid="summary-load-btn">
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Load
                </button>
                <button onClick={handleDownloadSummary} disabled={summaryData.length === 0} className="flex items-center gap-2 bg-[#dc3545] hover:bg-[#c82333] text-white text-sm font-medium px-4 py-2 rounded transition-colors disabled:opacity-50" data-testid="summary-download-btn">
                  <Download size={14} /> Download Report
                </button>
              </div>
              {loading ? (
                <div className="text-center py-8 text-gray-500" data-testid="report-loading">Loading report...</div>
              ) : summaryData.length > 0 ? (
                <div className="border border-gray-200 rounded" data-testid="summary-table-container">
                  <table className="w-full text-sm" data-testid="summary-table">
                    <thead><tr className="bg-gray-50 border-b border-gray-200"><th className="text-left px-4 py-3 font-semibold text-gray-700">Source</th><th className="text-right px-4 py-3 font-semibold text-gray-700">Projects</th></tr></thead>
                    <tbody>
                      {summaryData.map((row, idx) => (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`report-row-${idx}`}>
                          <td className="px-4 py-3 text-gray-800">{row.source}</td>
                          <td className="px-4 py-3 text-right text-gray-800">{row.projects.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="bg-gray-50 border-t border-gray-300"><td className="px-4 py-3 font-bold text-gray-900" data-testid="report-total-label">Total:</td><td className="px-4 py-3 text-right font-bold text-gray-900" data-testid="report-total-value">{summaryTotal.toLocaleString()}</td></tr></tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400" data-testid="report-empty">Click "Load" to generate the report.</div>
              )}
            </div>
          </div>
        )}

        {/* Detail Report */}
        {selectedReport === 'project-source-detail' && (
          <div className="bg-white rounded shadow p-8 lg:p-12 mt-4" data-testid="project-source-detail-view">
            <div className="text-center mb-8">
              <h3 className="text-2xl lg:text-3xl font-bold text-[#5B8DB8] italic mb-2" data-testid="detail-report-title">Projects by Source - Detail</h3>
              <p className="text-sm text-gray-500 mb-5" data-testid="detail-report-desc">Select the start and end dates for the range you want to analyze.</p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
                <div className="flex items-center gap-2 bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm" data-testid="detail-date-range">
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-transparent border-none outline-none text-sm text-gray-700" data-testid="detail-date-from" />
                  <span className="text-gray-400">-</span>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-transparent border-none outline-none text-sm text-gray-700" data-testid="detail-date-to" />
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 mb-6">
                <button onClick={handleLoad} disabled={loading} className="flex items-center gap-2 bg-[#8DB600] hover:bg-[#7BA300] text-white text-sm font-semibold px-5 py-2 rounded transition-colors" data-testid="detail-load-btn">
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Load
                </button>
                <button onClick={handleDownloadDetail} disabled={detailSources.length === 0} className="flex items-center gap-2 bg-[#dc3545] hover:bg-[#c82333] text-white text-sm font-semibold px-5 py-2 rounded transition-colors disabled:opacity-50" data-testid="detail-download-btn">Download Report</button>
              </div>
            </div>
            {loading ? (
              <div className="text-center py-8 text-gray-500" data-testid="detail-loading">Loading report...</div>
            ) : detailSources.length > 0 ? (
              <div data-testid="detail-table-container">
                <div className="border-b-2 border-gray-800 pb-2 mb-0">
                  <div className="grid grid-cols-12 text-sm font-bold text-gray-800">
                    <div className="col-span-5 px-2">Project Number</div>
                    <div className="col-span-4 px-2">Created By</div>
                    <div className="col-span-3 px-2 text-right">Created On</div>
                  </div>
                </div>
                {detailSources.map((src, srcIdx) => (
                  <div key={srcIdx} data-testid={`detail-source-group-${srcIdx}`}>
                    <div className="bg-gray-200 px-2 py-3 cursor-pointer hover:bg-gray-300 transition-colors flex items-center gap-2 border-b border-gray-300"
                      onClick={() => toggleSource(srcIdx)} data-testid={`detail-source-toggle-${srcIdx}`}>
                      <span className="inline-flex items-center justify-center w-5 h-5 border border-gray-500 text-gray-600 text-xs flex-shrink-0">
                        {expandedSources[srcIdx] ? <Minus size={12} /> : <Plus size={12} />}
                      </span>
                      <span className="text-sm font-bold text-gray-900" data-testid={`detail-source-name-${srcIdx}`}>
                        <span className="uppercase">{src.source}</span> - {src.count} project(s)
                      </span>
                    </div>
                    {expandedSources[srcIdx] && (
                      <div data-testid={`detail-source-projects-${srcIdx}`}>
                        {src.projects.map((proj, projIdx) => (
                          <div key={projIdx} className="grid grid-cols-12 text-sm py-3 border-b border-gray-100 hover:bg-gray-50">
                            <div className="col-span-5 px-2 pl-10 text-gray-800">{proj.project_number}</div>
                            <div className="col-span-4 px-2 text-gray-800">{proj.created_by}</div>
                            <div className="col-span-3 px-2 text-right text-gray-800">{proj.created_on}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="mt-4 text-sm text-gray-600" data-testid="detail-entries-count">
                  Showing 1 to {detailTotal.toLocaleString()} of {detailTotal.toLocaleString()} entries
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400" data-testid="detail-empty">Click "Load" to generate the report.</div>
            )}
          </div>
        )}

        {/* Note Tracking Report (Reconstruction / Mitigation) */}
        {isNoteTracking && (
          <div className="bg-white rounded shadow p-8 lg:p-12 mt-4" data-testid="note-tracking-view">
            <div className="text-center mb-8">
              <h3 className="text-2xl lg:text-3xl font-bold text-[#5B8DB8] italic mb-2" data-testid="note-tracking-title">
                {noteTrackingTitle}
              </h3>
              <p className="text-sm text-gray-500 mb-5" data-testid="note-tracking-desc">
                Select one or more project types to generate the report.
              </p>

              <div className="max-w-md mx-auto space-y-2 mb-6">
                <MultiSelectDropdown
                  options={projectTypes}
                  selected={selectedTypes}
                  onChange={setSelectedTypes}
                  placeholder="Select Project Types"
                  testIdPrefix="note-types"
                />
                <SearchableDropdown
                  options={daysOptions}
                  value={selectedDays}
                  onChange={setSelectedDays}
                  placeholder="Past 5+ days"
                  testIdPrefix="note-days"
                />
              </div>

              <div className="flex items-center justify-center gap-2 mb-6">
                <button onClick={handleLoad} disabled={loading}
                  className="flex items-center gap-2 bg-[#8DB600] hover:bg-[#7BA300] text-white text-sm font-semibold px-5 py-2 rounded transition-colors"
                  data-testid="note-load-btn">
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Load
                </button>
                <button onClick={handleDownloadNoteTracking}
                  className="flex items-center gap-2 bg-[#dc3545] hover:bg-[#c82333] text-white text-sm font-semibold px-5 py-2 rounded transition-colors"
                  data-testid="note-download-btn">
                  Download Report
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500" data-testid="note-loading">Loading report...</div>
            ) : noteLoaded && noteData && noteData.length === 0 ? (
              <div className="bg-[#F0AD4E] text-white px-6 py-4 rounded flex items-center gap-3" data-testid="note-no-data-warning">
                <Info size={18} />
                <span className="text-sm">Sorry, no data found for the selected date range. Please adjust your dates and try again.</span>
              </div>
            ) : noteData && noteData.length > 0 ? (
              <div className="border border-gray-200 rounded overflow-x-auto" data-testid="note-table-container">
                <table className="w-full text-sm" data-testid="note-table">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Project ID</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Customer</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Job Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Location</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Last Note</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {noteData.map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`note-row-${idx}`}>
                        <td className="px-4 py-3 text-gray-800">{row.project_id}</td>
                        <td className="px-4 py-3 text-gray-800">{row.customer}</td>
                        <td className="px-4 py-3 text-gray-800">{row.job_type}</td>
                        <td className="px-4 py-3 text-gray-800">{row.job_location}{row.address ? `, ${row.address}` : ''}</td>
                        <td className="px-4 py-3 text-gray-800 whitespace-nowrap">{row.last_note_action}</td>
                        <td className="px-4 py-3 text-gray-800 max-w-xs truncate">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
